import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link2, Search, FileText, Plus, X, Loader2, StickyNote, FolderOpen, ChevronRight, Folder, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useKnowledgeBaseList } from "@/contexts/KnowledgeBaseContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { foldersService, type LibraryFolder } from "@/services/foldersService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { cn } from "@/lib/utils";

interface DocumentLinkerDrawerProps {
  topicId: string;
  spaceId?: string; // Optional space ID to filter documents
  currentLinkedDocumentIds?: string[];
  trigger?: React.ReactNode;
  onLinksChanged?: () => void;
}

export function DocumentLinkerDrawer({
  topicId,
  spaceId,
  currentLinkedDocumentIds = [],
  trigger,
  onLinksChanged
}: DocumentLinkerDrawerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspaceId } = useWorkspace();

  // Reset folder navigation when drawer closes
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      // Reset state when drawer closes
      setExpandedFolderIds(new Set());
      setSearch("");
    }
  };
  
  const toggleFolderExpanded = (folderId: string) => {
    setExpandedFolderIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  // Fetch library folders (scoped by current workspace or personal)
  const { data: folders = [], isLoading: loadingFolders } = useQuery<LibraryFolder[]>({
    queryKey: ["/api/library-folders", currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      return await foldersService.getFolders(currentWorkspaceId ?? null);
    },
    enabled: open,
  });

  const {
    items: allDocuments,
    isLoading: loadingDocuments,
    refresh: refreshKnowledge,
  } = useKnowledgeBaseList({ enabled: open });

  useEffect(() => {
    if (open) {
      refreshKnowledge();
    }
  }, [open, refreshKnowledge]);

  // Helper function to get topicIds from document (supports both array and single value for backward compatibility)
  const getDocumentTopicIds = (doc: any): string[] => {
    const metadata = doc.metadata || {};
    const parsedMetadata = typeof metadata === 'string' ? (() => {
      try { return JSON.parse(metadata); } catch { return {}; }
    })() : metadata;
    
    // Check for topicIds array first (new format)
    if (Array.isArray(parsedMetadata.topicIds)) {
      return parsedMetadata.topicIds.map((id: any) => String(id)).filter((id: string) => id !== 'null' && id !== 'undefined');
    }
    
    // Fallback to single topicId in metadata (backward compatibility - more reliable than doc.topicId)
    if (parsedMetadata.topicId) {
      return [String(parsedMetadata.topicId)];
    }
    
    return [];
  };

  // Helper function to get spaceIds from document (supports both array and single value for backward compatibility)
  const getDocumentSpaceIds = (doc: any): string[] => {
    const metadata = doc.metadata || {};
    const parsedMetadata = typeof metadata === 'string' ? (() => {
      try { return JSON.parse(metadata); } catch { return {}; }
    })() : metadata;
    
    // Check for spaceIds array first (new format)
    if (Array.isArray(parsedMetadata.spaceIds)) {
      return parsedMetadata.spaceIds.map((id: any) => String(id)).filter((id: string) => id !== 'null' && id !== 'undefined');
    }
    
    // Fallback to single spaceId in metadata (backward compatibility - more reliable than doc.spaceId)
    if (parsedMetadata.spaceId) {
      return [String(parsedMetadata.spaceId)];
    }
    
    // Last fallback to doc.spaceId (for very old documents)
    if (doc.spaceId) {
      return [String(doc.spaceId)];
    }
    
    return [];
  };

  // Get documents linked to the CURRENT topic (the one we're linking to)
  // Support both topicIds array (new) and topicId single value (backward compatibility)
  const currentTopicLinkedDocumentIds = useMemo(() => {
    const linked = new Set<string>();
    const topicIdStr = String(topicId);
    
    // Check all documents - if they have topicId or topicIds matching the current topic, they're linked
    allDocuments.forEach(doc => {
      const docTopicIds = getDocumentTopicIds(doc);
      if (docTopicIds.some(id => String(id) === topicIdStr)) {
        linked.add(doc.id);
      }
    });
    
    // Also include currentLinkedDocumentIds if provided
    if (currentLinkedDocumentIds) {
      currentLinkedDocumentIds.forEach(id => linked.add(id));
    }
    return linked;
  }, [allDocuments, topicId, currentLinkedDocumentIds]);

  // Get root folders using foldersService.buildFolderTree (matching LibraryTree)
  const rootFolders = useMemo(() => {
    return foldersService.buildFolderTree(folders);
  }, [folders]);

  // Get root-level documents (documents without folders) - matching LibraryTree pattern
  const rootLevelDocuments = useMemo(() => {
    return allDocuments.filter(doc => !doc.folderId);
  }, [allDocuments]);

  // Get documents in a specific folder
  const getDocumentsInFolder = (folderId: string | null) => {
    if (folderId === null) {
      // Return root-level documents
      return rootLevelDocuments;
    }
    return allDocuments.filter(doc => doc.folderId === folderId);
  };

  // Get child folders using foldersService.getChildFolders (matching LibraryTree)
  const getChildFolders = (parentId: string) => {
    return foldersService.getChildFolders(folders, parentId);
  };

  // Filter documents by search
  const filteredDocuments = useMemo(() => {
    if (!search.trim()) return allDocuments;
    
    const q = search.toLowerCase();
    return allDocuments.filter(
      (doc) =>
        doc.title?.toLowerCase().includes(q) ||
        doc.type?.toLowerCase().includes(q)
    );
  }, [allDocuments, search]);

  const linkMutation = useMutation({
    mutationFn: async (documentId: string) => {
      // Get the document to check its current topicIds and spaceIds
      const doc = allDocuments.find(d => d.id === documentId);
      if (!doc) {
        // If document not in cache, fetch it
        const fetchedDoc = await knowledgeBaseService.getKnowledgeBaseItem(documentId);
        if (!fetchedDoc) throw new Error("Document not found");
        
        const currentTopicIds = getDocumentTopicIds(fetchedDoc);
        const newTopicIds = currentTopicIds.includes(String(topicId))
          ? currentTopicIds
          : [...currentTopicIds, String(topicId)];
        
        // Also link the space if spaceId is provided and document doesn't already have it
        const updateData: any = { topicIds: newTopicIds };
        if (spaceId) {
          const currentSpaceIds = getDocumentSpaceIds(fetchedDoc);
          if (!currentSpaceIds.includes(String(spaceId))) {
            updateData.spaceIds = [...currentSpaceIds, String(spaceId)];
          }
        }
        
        return await knowledgeBaseService.updateKnowledgeBaseItem(documentId, updateData);
      }
      
      // Get current topicIds and spaceIds (supports both array and single value)
      const currentTopicIds = getDocumentTopicIds(doc);
      const currentSpaceIds = getDocumentSpaceIds(doc);
      
      // Add the topic to the array if it's not already there
      const newTopicIds = currentTopicIds.includes(String(topicId))
        ? currentTopicIds
        : [...currentTopicIds, String(topicId)];
      
      // Also link the space if spaceId is provided and document doesn't already have it
      const updateData: any = { topicIds: newTopicIds };
      if (spaceId) {
        if (!currentSpaceIds.includes(String(spaceId))) {
          updateData.spaceIds = [...currentSpaceIds, String(spaceId)];
        }
      }
      
      // Link document to topic (and space if needed) by updating its arrays
      return await knowledgeBaseService.updateKnowledgeBaseItem(documentId, updateData);
    },
    onSuccess: async (updatedDoc) => {
      if (!updatedDoc) return;
      
      // Optimistically update the cache immediately to prevent UI flicker
      queryClient.setQueriesData<any[]>(
        { queryKey: ["/api/knowledgebase"], exact: false },
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map(doc => {
            if (doc.id === updatedDoc.id) {
              const metadata = typeof doc.metadata === 'string' 
                ? (() => { try { return JSON.parse(doc.metadata); } catch { return {}; } })()
                : (doc.metadata || {});
              const currentTopicIds = getDocumentTopicIds(doc);
              const newTopicIds = currentTopicIds.includes(String(topicId))
                ? currentTopicIds
                : [...currentTopicIds, String(topicId)];
              
              // Also update spaceIds if spaceId is provided
              const currentSpaceIds = getDocumentSpaceIds(doc);
              const newSpaceIds = spaceId && !currentSpaceIds.includes(String(spaceId))
                ? [...currentSpaceIds, String(spaceId)]
                : currentSpaceIds;
              
              return {
                ...doc,
                metadata: {
                  ...metadata,
                  topicIds: newTopicIds,
                  topicId: null, // Clear old topicId when using topicIds array
                  spaceIds: newSpaceIds,
                  spaceId: newSpaceIds.length > 0 ? null : metadata.spaceId // Clear old spaceId when using spaceIds array
                },
                topicId: null,
                spaceId: newSpaceIds.length > 0 ? null : doc.spaceId
              };
            }
            return doc;
          });
        }
      );

      // Refetch to ensure consistency without clearing cache
      await queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false });
      
      // Invalidate knowledgebase-summary to refresh KnowledgeBaseContext
      queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
      
      // Don't invalidate topics or thread queries - they don't change when documents are linked
      // This prevents TopicsManagement from re-rendering and losing expanded state
      
      // Call onLinksChanged which should use refetchQueries
      onLinksChanged?.();
      
      const spaceMessage = spaceId ? " and its space" : "";
      toast({
        title: "Document linked",
        description: `Document has been linked to this topic${spaceMessage}`,
      });
    },
    onError: async (error: any) => {
      // Refetch on error to restore correct state
      await queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false });
      toast({
        title: "Failed to link document",
        description: error.message || "Something went wrong",
        variant: "destructive"
      });
    }
  });

  const unlinkMutation = useMutation({
    mutationFn: async (documentId: string) => {
      // Get the document to check its current topicIds
      const doc = allDocuments.find(d => d.id === documentId);
      if (!doc) {
        // If document not in cache, fetch it
        const fetchedDoc = await knowledgeBaseService.getKnowledgeBaseItem(documentId);
        if (!fetchedDoc) throw new Error("Document not found");
        
        const currentTopicIds = getDocumentTopicIds(fetchedDoc);
        const newTopicIds = currentTopicIds.filter(id => String(id) !== String(topicId));
        
        return await knowledgeBaseService.updateKnowledgeBaseItem(documentId, {
          topicIds: newTopicIds
        });
      }
      
      // Get current topicIds (supports both array and single value)
      const currentTopicIds = getDocumentTopicIds(doc);
      
      // Remove the topic from the array
      const newTopicIds = currentTopicIds.filter(id => String(id) !== String(topicId));
      
      // Unlink document from topic by updating its topicIds array
      return await knowledgeBaseService.updateKnowledgeBaseItem(documentId, {
        topicIds: newTopicIds
      });
    },
    onSuccess: async (updatedDoc) => {
      if (!updatedDoc) return;
      
      // Optimistically update the cache immediately to prevent UI flicker
      queryClient.setQueriesData<any[]>(
        { queryKey: ["/api/knowledgebase"], exact: false },
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map(doc => {
            if (doc.id === updatedDoc.id) {
              const metadata = typeof doc.metadata === 'string' 
                ? (() => { try { return JSON.parse(doc.metadata); } catch { return {}; } })()
                : (doc.metadata || {});
              const currentTopicIds = getDocumentTopicIds(doc);
              const newTopicIds = currentTopicIds.filter(id => String(id) !== String(topicId));
              
              return {
                ...doc,
                metadata: {
                  ...metadata,
                  topicIds: newTopicIds,
                  topicId: null // Clear old topicId when using topicIds array
                },
                topicId: null
              };
            }
            return doc;
          });
        }
      );

      // Refetch to ensure consistency without clearing cache
      await queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false });
      
      // Invalidate knowledgebase-summary to refresh KnowledgeBaseContext
      queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
      
      // Don't invalidate topics or thread queries - they don't change when documents are unlinked
      // This prevents TopicsManagement from re-rendering and losing expanded state
      
      // Call onLinksChanged which should use refetchQueries
      onLinksChanged?.();
      
      toast({
        title: "Document unlinked",
        description: "Document has been unlinked from this topic",
      });
    },
    onError: async (error: any) => {
      // Refetch on error to restore correct state
      await queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false });
      toast({
        title: "Failed to unlink document",
        description: error.message || "Something went wrong",
        variant: "destructive"
      });
    }
  });

  const handleToggleDocument = (documentId: string, isLinked: boolean, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (isLinked) {
      unlinkMutation.mutate(documentId);
    } else {
      linkMutation.mutate(documentId);
    }
  };

  const isLoading = loadingDocuments;
  const isPending = linkMutation.isPending || unlinkMutation.isPending;

  // Recursive folder tree component - matches LibraryTree structure
  const FolderTreeItem = ({ folder, level = 0 }: { folder: LibraryFolder; level?: number }) => {
    const childFolders = getChildFolders(folder.id);
    const hasChildren = childFolders.length > 0;
    const documents = getDocumentsInFolder(folder.id);
    const itemCount = documents.length;
    const hasItems = itemCount > 0;
    const isExpanded = expandedFolderIds.has(folder.id);

    return (
      <div>
        <div
          className={cn(
            "group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors min-w-0"
          )}
          style={{ paddingLeft: level > 0 ? `${level * 16}px` : undefined }}
          onClick={() => toggleFolderExpanded(folder.id)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 p-0 shrink-0 hover:bg-accent/50 opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              toggleFolderExpanded(folder.id);
            }}
          >
            {(hasChildren || hasItems) ? (
              isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
            )}
          </Button>

          {isExpanded ? (
            <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" />
          ) : (
            <Folder className="h-4 w-4 text-amber-500 shrink-0" />
          )}

          <span className="text-sm truncate flex-1 min-w-0">{folder.name}</span>
          
          {itemCount > 0 && (
            <Badge variant="secondary" className="text-xs h-5 px-1.5 shrink-0">
              {itemCount}
            </Badge>
          )}
        </div>

        {isExpanded && (
          <div className="mt-1">
            {/* Render child folders first - matching LibraryTree */}
            {hasChildren && (
              <div>
                {childFolders.map((child) => (
                  <FolderTreeItem key={child.id} folder={child} level={level + 1} />
                ))}
              </div>
            )}
            
            {/* Then render documents in this folder */}
            {hasItems && (
              <div className="space-y-0.5">
                {documents.map((document) => {
                  const isLinkedToCurrentTopic = currentTopicLinkedDocumentIds.has(document.id);

                  return (
                    <div
                      key={document.id}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors min-w-0 group cursor-pointer",
                        isLinkedToCurrentTopic && "bg-primary/5"
                      )}
                      style={{ paddingLeft: `${(level + 1) * 16 + 18}px` }}
                      onClick={() => handleToggleDocument(document.id, isLinkedToCurrentTopic)}
                      data-testid={`document-item-${document.id}`}
                    >
                      <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="text-xs truncate flex-1 min-w-0">{document.title || "Untitled Document"}</span>
                      
                      <div className="flex items-center gap-1 shrink-0">
                        {document.type && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1">
                            {document.type}
                          </Badge>
                        )}
                        {isLinkedToCurrentTopic && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1 text-primary border-primary/30">
                            Linked
                          </Badge>
                        )}
                      {/*  {isLinkedToCurrentTopic ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleDocument(document.id, true, e);
                            }}
                            disabled={isPending}
                            data-testid={`button-unlink-${document.id}`}
                            title="Unlink from topic"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleDocument(document.id, false, e);
                            }}
                            disabled={isPending}
                            data-testid={`button-link-${document.id}`}
                            title="Link to topic"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        )}*/}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Show empty folder message if no children or items */}
            {!hasChildren && !hasItems && (
              <div className="px-2 py-1 text-xs text-muted-foreground italic" style={{ paddingLeft: `${(level + 1) * 16 + 18}px` }}>
                Empty folder
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="button-link-documents">
            <Link2 className="h-4 w-4" />
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Link Notes & Documents to Topic
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="input-search-documents"
            />
          </div>

          <ScrollArea className="h-[calc(100vh-200px)]">
            {isLoading || loadingFolders || loadingDocuments ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : search.trim() ? (
              // Show flat search results - matching LibraryTree styling
              <div className="space-y-0.5 pr-4">
                {filteredDocuments.length === 0 ? (
                  <div className="px-2 py-8 text-sm text-muted-foreground text-center">
                    No documents match your search
                  </div>
                ) : (
                  filteredDocuments.map((document) => {
                    const isLinkedToCurrentTopic = currentTopicLinkedDocumentIds.has(document.id);

                    return (
                      <div
                        key={document.id}
                        className={cn(
                          "flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors min-w-0 group cursor-pointer",
                          isLinkedToCurrentTopic && "bg-primary/5"
                        )}
                        onClick={() => handleToggleDocument(document.id, isLinkedToCurrentTopic)}
                        data-testid={`document-item-${document.id}`}
                      >
                        <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="text-xs truncate flex-1 min-w-0">{document.title || "Untitled Document"}</span>
                        
                        <div className="flex items-center gap-1 shrink-0">
                          {document.type && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1">
                              {document.type}
                            </Badge>
                          )}
                          {isLinkedToCurrentTopic && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1 text-primary border-primary/30">
                              Linked
                            </Badge>
                          )}
                          {isLinkedToCurrentTopic ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleDocument(document.id, true, e);
                              }}
                              disabled={isPending}
                              data-testid={`button-unlink-${document.id}`}
                              title="Unlink from topic"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleDocument(document.id, false, e);
                              }}
                              disabled={isPending}
                              data-testid={`button-link-${document.id}`}
                              title="Link to topic"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              // Show tree structure - matching LibraryTree layout
              <div className="space-y-0.5 pr-4">
                {rootFolders.length === 0 && rootLevelDocuments.length === 0 ? (
                  <div className="px-2 py-8 text-sm text-muted-foreground text-center">
                    No documents available. Create notes in the Documents sidebar to link them to topics.
                  </div>
                ) : (
                  <>
                    {/* Render folder tree first */}
                    {rootFolders.length > 0 && (
                      <div>
                        {rootFolders.map((folder) => (
                          <FolderTreeItem key={folder.id} folder={folder} level={0} />
                        ))}
                      </div>
                    )}

                    {/* Always render root-level documents if they exist - matching LibraryTree */}
                    {rootLevelDocuments.length > 0 && (
                      <div className="space-y-0.5">
                        {rootLevelDocuments.map((document) => {
                          const isLinkedToCurrentTopic = currentTopicLinkedDocumentIds.has(document.id);

                          return (
                            <div
                              key={document.id}
                              className={cn(
                                "flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors min-w-0 group cursor-pointer",
                                isLinkedToCurrentTopic && "bg-primary/5"
                              )}
                              style={{ paddingLeft: "18px" }}
                              onClick={() => handleToggleDocument(document.id, isLinkedToCurrentTopic)}
                              data-testid={`document-item-${document.id}`}
                            >
                              <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                              <span className="text-xs truncate flex-1 min-w-0">{document.title || "Untitled Document"}</span>
                              
                              <div className="flex items-center gap-1 shrink-0">
                                {document.type && (
                                  <Badge variant="outline" className="text-[10px] h-4 px-1">
                                    {document.type}
                                  </Badge>
                                )}
                                {isLinkedToCurrentTopic && (
                                  <Badge variant="outline" className="text-[10px] h-4 px-1 text-primary border-primary/30">
                                    Linked
                                  </Badge>
                                )}
                                {isLinkedToCurrentTopic ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 opacity-0 group-hover:opacity-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleDocument(document.id, true, e);
                                    }}
                                    disabled={isPending}
                                    data-testid={`button-unlink-${document.id}`}
                                    title="Unlink from topic"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 opacity-0 group-hover:opacity-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleDocument(document.id, false, e);
                                    }}
                                    disabled={isPending}
                                    data-testid={`button-link-${document.id}`}
                                    title="Link to topic"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}

